/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Users/AdminUserTable.tsx.
 */

import React, { useEffect, useState, useMemo } from "react";
import axiosClient from "../../../../api/axiosClient";
import DataTable, { Column } from "../../../../components/common/DataTable";
import "./AdminUserTable.css";
import { User } from "../../../../interfaces/userInterface";

const AdminUserTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadUsers = async () => {
    try {
      const response = await axiosClient.get("/admin/users");
      if (Array.isArray(response.data)) setUsers(response.data);
      else if (Array.isArray(response.data?.data)) setUsers(response.data.data);
    } catch (err) {
      console.error("❌ Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Column definitions
  const columns: Column<User>[] = [
    {
      key: "name",
      label: "Full Name",
      render: (item) => (
        <div className="user-info">
          <img
            src={`https://api.dicebear.com/8.x/initials/svg?seed=${item.name}`}
            alt={item.name}
            className="user-avatar small"
          />
          <span style={{ fontWeight: 600, color: "#2d2730" }}>{item.name}</span>
        </div>
      ),
      width: "18%",
    },
    {
      key: "email",
      label: "Email",
      render: (item) => (
        <div style={{ color: "#2d2730" }}>{item.email}</div>
      ),
      width: "20%",
    },
    {
      key: "username",
      label: "Username",
      render: (item) => (
        <span style={{ color: "#6b6b6b" }}>
          {item.name || item.email.split("@")[0]}
        </span>
      ),
      width: "15%",
    },
    {
      key: "role",
      label: "Role",
      render: (item) => (
        <span style={{ fontWeight: 500, color: "#2d2730" }}>
          {item.role || "-"}
        </span>
      ),
      width: "12%",
    },
    {
      key: "createdAt",
      label: "Joined Date",
      render: (item) => (
        <div>
          <div>{new Date(item.createdAt).toLocaleDateString()}</div>
          <div style={{ fontSize: "0.85rem", color: "#6b6b6b", marginTop: "2px" }}>
            {new Date(item.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      ),
      width: "15%",
    },
  ];

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      result = result.filter((user) => {
        const name = user.name?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";
        const role = user.role?.toLowerCase() || "";
        return name.includes(searchLower) || 
               email.includes(searchLower) || 
               role.includes(searchLower);
      });
    }

    return result;
  }, [users, search]);

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Manage Users</span>
      </div>
      <div className="admin-user-page">
      {/* Custom Header: Search and Filter on left */}
      <div className="admin-user-header">
        <div className="admin-user-left">
          <div className="data-table-search">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search users by name, email, or role..."
              className="data-table-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
      <DataTable<User>
        data={filteredUsers}
        columns={columns}
        loading={loading}
        pagination={true}
        itemsPerPage={10}
        emptyMessage="No users found"
      />
      </div>
    </div>
  );
};

/** React component — Admin User Table. */
export default AdminUserTable;
